/**
 * Industry Updates Page
 *
 * Full CRUD management for industry updates with web-grounded AI drafting,
 * source tracking, and image attachments. The heavy lifting lives in
 * UpdateWorkbench; this page handles list, filters, and delete.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Search, Edit, Trash2,
  Loader2, Building2, Calendar, Shield,
  Sparkles, Globe, Target, TrendingUp, BookOpen,
  Newspaper, Link as LinkIcon, Image as ImageIcon,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import type { BadgeVariant } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import {
  getIndustryUpdates,
  createIndustryUpdate,
  updateIndustryUpdate,
  deleteIndustryUpdate,
  getCategoryLabel,
  getSeverityVariant,
} from '../services/industryUpdates';
import { getTenants } from '../services/tenants';
import type {
  IndustryUpdate,
  IndustryCategory,
  IndustrySeverity,
  Tenant,
} from '../data/types';
import { cn } from '../lib/utils';
import { UpdateWorkbench, type WorkbenchPayload } from '../components/industry-updates/UpdateWorkbench';
import { industryLabel } from '../lib/industries';

// ============================================================================
// Constants
// ============================================================================

interface CategoryOption {
  value: IndustryCategory;
  label: string;
  icon: React.ReactNode;
}

const CATEGORIES: CategoryOption[] = [
  { value: 'patch_alert', label: 'Patch Alert', icon: <Shield className="w-4 h-4" /> },
  { value: 'trend_analysis', label: 'Trend Analysis', icon: <TrendingUp className="w-4 h-4" /> },
  { value: 'global_context', label: 'Global Context', icon: <Globe className="w-4 h-4" /> },
  { value: 'best_practice', label: 'Best Practice', icon: <BookOpen className="w-4 h-4" /> },
];

function severityBorderClass(severity: IndustrySeverity | string | null): string {
  switch (severity) {
    case 'critical': return 'border-l-red-500';
    case 'high': return 'border-l-orange-500';
    case 'medium': return 'border-l-yellow-500';
    case 'low': return 'border-l-green-500';
    case 'informational': return 'border-l-blue-500';
    default: return 'border-l-slate-400';
  }
}

// ============================================================================
// Main Page
// ============================================================================

export function IndustryUpdates() {
  const [updates, setUpdates] = useState<IndustryUpdate[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<IndustryCategory | ''>('');
  const [showInactive, setShowInactive] = useState(false);

  const [workbenchOpen, setWorkbenchOpen] = useState(false);
  const [editing, setEditing] = useState<IndustryUpdate | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // ---- Data loading ----

  const loadData = useCallback(async () => {
    setLoading(true);
    const [updatesResult, tenantsResult] = await Promise.all([
      getIndustryUpdates({
        includeInactive: showInactive,
        category: filterCategory || undefined,
      }),
      getTenants(),
    ]);

    if (updatesResult.success) setUpdates(updatesResult.data.updates);
    if (tenantsResult.success) setTenants(tenantsResult.data);
    setLoading(false);
  }, [showInactive, filterCategory]);

  useEffect(() => { loadData(); }, [loadData]);

  // ---- Handlers ----

  const handleOpenCreate = () => {
    setEditing(null);
    setWorkbenchOpen(true);
  };

  const handleOpenEdit = (update: IndustryUpdate) => {
    setEditing(update);
    setWorkbenchOpen(true);
  };

  const handleDelete = async (updateId: string) => {
    if (!confirm('Are you sure you want to deactivate this update?')) return;
    setDeleting(updateId);
    const result = await deleteIndustryUpdate(updateId);
    if (result.success) {
      setUpdates(prev => prev.filter(u => u.updateId !== updateId));
    }
    setDeleting(null);
  };

  const handleSave = async (payload: WorkbenchPayload, updateId: string | null) => {
    if (updateId) {
      const res = await updateIndustryUpdate(updateId, payload);
      if (res.success) {
        setUpdates(prev => prev.map(u => u.updateId === updateId ? res.data : u));
        setWorkbenchOpen(false);
      }
    } else {
      const res = await createIndustryUpdate({
        tenantId: payload.tenantId,
        title: payload.title,
        content: payload.content,
        category: payload.category,
        severity: payload.severity || undefined,
        effectiveFrom: payload.effectiveFrom,
        industries: payload.industries,
        sources: payload.sources,
        images: payload.images,
        searchQuery: payload.searchQuery,
        generationModel: payload.generationModel,
      });
      if (res.success) {
        setUpdates(prev => [res.data, ...prev]);
        setWorkbenchOpen(false);
      }
    }
  };

  // ---- Filtering ----

  const filteredUpdates = updates.filter(u => {
    if (search) {
      const q = search.toLowerCase();
      if (!u.title.toLowerCase().includes(q) && !u.content.toLowerCase().includes(q)) {
        return false;
      }
    }
    return true;
  });

  // ---- Render ----

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
            <Newspaper className="w-6 h-6" />
            Industry Updates
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Manage threat advisories, global context stats, and top threats for customer reports
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleOpenCreate}>
            <Sparkles className="w-4 h-4 mr-1" />
            New Update
          </Button>
        </div>
      </div>

      {/* Filters bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder="Search updates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-bg-secondary border border-border-default rounded-lg text-sm focus:outline-none focus:border-accent-blue"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value as IndustryCategory | '')}
          className="px-3 py-2 bg-bg-secondary border border-border-default rounded-lg text-sm focus:outline-none focus:border-accent-blue"
        >
          <option value="">All Categories</option>
          {CATEGORIES.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-text-secondary whitespace-nowrap">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-border-default"
          />
          Inactive
        </label>
        <Button variant="ghost" onClick={loadData} title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Updates list */}
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      ) : filteredUpdates.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Newspaper className="w-12 h-12 mx-auto text-text-muted mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">No Updates</h3>
            <p className="text-sm text-text-secondary mb-6">
              Create your first industry update — the workbench can generate a grounded draft from a web search.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={handleOpenCreate}>
                <Sparkles className="w-4 h-4 mr-1" />
                New Update
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredUpdates.map((update) => {
            const sourceCount = update.sources?.length ?? 0;
            const imageCount = update.images?.length ?? 0;
            return (
              <Card
                key={update.updateId}
                className={cn('border-l-4', severityBorderClass(update.severity))}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-medium text-text-primary">{update.title}</h3>
                        <Badge variant={update.isActive ? 'green' : 'default'}>
                          {update.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        {update.severity && (
                          <Badge variant={getSeverityVariant(update.severity) as BadgeVariant} className="capitalize">
                            {update.severity}
                          </Badge>
                        )}
                        <Badge variant="outline">
                          {getCategoryLabel(update.category)}
                        </Badge>
                        {sourceCount > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <LinkIcon className="w-3 h-3 mr-1" /> {sourceCount}
                          </Badge>
                        )}
                        {imageCount > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <ImageIcon className="w-3 h-3 mr-1" /> {imageCount}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-text-secondary mb-2 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {update.tenantDisplayName || 'Global'}
                        </span>
                        {!update.tenantId && (
                          <span className="flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            {!update.industries || update.industries.length === 0
                              ? 'All industries'
                              : update.industries.map(industryLabel).join(', ')}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(parseISO(update.effectiveFrom), 'MMM d, yyyy')}
                        </span>
                        <span>by {update.createdBy}</span>
                      </div>
                      <p className="text-sm text-text-secondary line-clamp-2">
                        {update.content}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(update)} title="Edit">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(update.updateId)}
                        disabled={deleting === update.updateId}
                        title="Delete"
                      >
                        {deleting === update.updateId ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 text-red-500" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Workbench panel */}
      {workbenchOpen && (
        <UpdateWorkbench
          key={editing?.updateId ?? 'new'}
          isOpen={workbenchOpen}
          onClose={() => setWorkbenchOpen(false)}
          editing={editing}
          tenants={tenants}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
